import {
  deleteNotification,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  Notification,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

export function useNotificationsButton() {
  const { lang } = useContext(PspContext);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>();

  function updateHasNewNotifications() {
    getUnreadNotificationCount().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setHasNewNotification(data > 0);
      }
    });
  }

  useEffect(() => {
    updateHasNewNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getNotifications({ query: { limit: 100, offset: 0 } }).then(
      ({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setNotifications(data);
        }
      },
    );
  }, [lang]);

  async function markAllAsRead() {
    const { error } = await markAllNotificationsAsRead();
    if (error) {
      toast(translate("somethingWentWrong", lang));
      throw new Error("Could not mark all notifications as read.");
    }
    setHasNewNotification(false);
    setNotifications((prev) =>
      prev?.map((n) => ({
        ...n,
        readAt: n.readAt ?? new Date().toDateString(),
      })),
    );
  }

  function setNotificationRead(notificationId: string, isRead: boolean) {
    setNotifications((prev) =>
      prev?.map((n) =>
        n.id === notificationId
          ? {
              ...n,
              readAt: isRead ? new Date().toISOString() : null,
            }
          : n,
      ),
    );
  }

  function removeNotification(notificationId: string) {
    deleteNotification({ path: { notification_id: notificationId } }).then(
      ({ error }) => {
        if (error || notifications === undefined) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setNotifications(
            notifications.filter((n) => n.id !== notificationId),
          );
          toast(translate("notificationRemovedSuccessfully", lang));
        }
      },
    );
  }

  const isInBackoffice = location.pathname.startsWith("/admin");

  return {
    lang,
    notificationsOpen,
    setNotificationsOpen,
    hasNewNotification,
    notifications,
    markAllAsRead,
    setNotificationRead,
    updateHasNewNotifications,
    removeNotification,
    isInBackoffice,
  };
}
